import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaArrowRight } from "react-icons/fa"; // Ensure react-icons is installed

gsap.registerPlugin(ScrollTrigger);

const ExpansionFrameworkSection = ({HOW_IT_WORKS , heading , subHeading , description}) => {
    const sectionRef = useRef(null);
    const headerRefs = useRef([]);
    const cardRefs = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 75%", // Triggers when top of section is 75% down viewport
                    toggleActions: "play none none none",
                }
            });

            // 1. Header Text Cascade
            tl.from(headerRefs.current, {
                y: 30,
                opacity: 0,
                duration: 0.6,
                stagger: 0.1,
                ease: "power2.out"
            });

            // 2. Loop through cards for detailed internal animation
            cardRefs.current.forEach((card, index) => {
                // Select internal elements using standard DOM selectors scoped to this card
                const icon = card.querySelector('.card-icon');
                const listItems = card.querySelectorAll('.list-item');
                
                // Animate the Card Container
                tl.from(card, {
                    y: 60,
                    opacity: 0,
                    duration: 0.6,
                    ease: "power2.out"
                }, index === 0 ? "-=0.2" : "-=0.4"); // Overlap slightly with previous

                // Icon Pop (Elastic)
                if (icon) {
                    tl.from(icon, {
                        scale: 0,
                        rotation: -20,
                        duration: 0.8,
                        ease: "elastic.out(1, 0.5)"
                    }, "<0.2"); // Start 0.2s after card starts moving
                }

                // List Items Slide In
                if (listItems.length > 0) {
                    tl.from(listItems, {
                        x: -20,
                        opacity: 0,
                        stagger: 0.05, // Fast ripple effect
                        duration: 0.4,
                        ease: "power1.out"
                    }, "<0.3");
                }
            });

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Helper to add refs safely
    const addToHeaderRefs = (el) => { if (el && !headerRefs.current.includes(el)) headerRefs.current.push(el); };
    const addToCardRefs = (el) => { if (el && !cardRefs.current.includes(el)) cardRefs.current.push(el); };

    return (
        <section ref={sectionRef} className='flex flex-col gap-y-5 container max-w-7xl mx-auto px-6 md:px-12 my-32'>
            {/* Headers */}
            <h2 ref={addToHeaderRefs} className='text-[#111827] text-center font-[600] leading-[40px] text-[40px]'>
                {heading}
            </h2>
            <h3 ref={addToHeaderRefs} className='text-[#111827] text-center font-[600] text-[23px] px-5 md:px-10 text-center'>
                {subHeading}
            </h3>
            <h4 ref={addToHeaderRefs} className='text-[#4B5563] font-[600] text-[18px] px-6 md:px-20 text-center'>
                {description}
            </h4>

            {/* Cards Grid */}
            <ol className='grid grid-cols-1 min-[830px]:grid-cols-2 gap-8 mt-9'>
                {HOW_IT_WORKS.map((item, index) => (
                    <li key={index} ref={addToCardRefs}>
                        <article className='flex flex-col gap-y-5 justify-center bg-[#E5E7EB] px-8 py-14 rounded-[15px] h-full transform transition-transform hover:-translate-y-1 duration-300'>
                            
                            {/* Header: Icon + Title */}
                            <div className='grid grid-cols-[max-content_1fr] items-center gap-x-9'>
                                <figure className='card-icon w-24 h-24 relative bg-[#FFFFFF] rounded-full flex items-center justify-center p-5 shadow-sm'>
                                    <img src={item.img} className='w-full h-full object-scale-down' alt={item.heading} />
                                </figure>
                                <figcaption className='flex flex-col justify-center gap-y-3'>
                                    <h4 className='text-[#000000] text-[27px] leading-[30px] font-[500]'>{item.heading}</h4>
                                    <h5 className='text-[#000000] text-[16px] leading-[20px] font-[600] opacity-80'>{item.description}</h5>
                                </figcaption>
                            </div>

                            {/* Content & List */}
                            <div className='flex flex-col gap-y-7 mt-2'>
                                <p className='font-[500] text-[17px] text-[#000000] leading-relaxed'>{item.content}</p>
                                
                                <div>
                                    <h5 className='font-[600] text-[#000000] text-[18px] mb-3'>What's included:</h5>
                                    <ol className='flex flex-col gap-y-2'>
                                        {item.points.map((point, i) => (
                                            <li className='flex gap-x-1 items-start' key={i}>
                                                <div className="h-6 w-6 rounded-full bg-yellow-500 flex items-center justify-center text-white mr-3">
                                                    <FaArrowRight className="text-[12px]" />
                                                </div>
                                                <p className='font-[500] text-[#000000] text-[17px]'>{point}</p>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            </div>
                        </article>
                    </li>
                ))}
            </ol>
        </section>
    );
};

export default ExpansionFrameworkSection;